import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import oIconLibrary from 'utils/iconLibrary';
import { fnAddGuestToEventInCart } from 'components/EventRegistration/redux/guestHelpers';
import {
  currentUserFirstLastNameSelector,
  currentUserLookupIdSelector,
  selectCurrentUserEmail,
} from 'redux/selectors';
import { selectEventRegNavSteps } from 'components/EventRegistration/EventRegistrationContainer/EventRegistrationNavBar/redux/selectors';
import Accordion from 'components/Layout/Accordion';
import useIsUserAuthenticated from 'utils/hooks/useIsUserAuthenticated';
import * as CheckoutActions from 'components/Checkout/redux/actions';
import {
  selectAllEventsInCart,
  selectSubEventsByMainEventLookupId,
} from 'components/EventRegistration/redux/selectors';
import { fnRemoveEventFromCart } from 'components/EventRegistration/redux/eventHelpers';
import EventDescription from '../EventDescription';
import SubEventDetailCard from './SubEventDetailCard';
import SelectedEventsList from './SelectedEventsList';
import { oSchema, fnBuildInitialValues, sFormId } from './helpers';
import EventDetailsPageHeader from '../EventDetailsPageHeader';
import MultiLevelEventDetailsFooter from './MultiLevelEventDetailsFooter';
import EventRegistrationWindowClose from '../EventRegistrationWindowClose';
import EventDetailsContext from '../EventDetailsContext';
import EventRefundPolicy from '../EventRefundPolicy';

const MultiLevelEventDetails = ({ fnSetShowSignInOrContinueAsGuestModal }) => {
  const { oEvent } = useContext(EventDetailsContext);
  const fnNavigate = useNavigate();
  const fnDispatch = useDispatch();
  const [bHasSubmittedForm, fnSetHasSubmittedForm] = useState(false);
  const sCurrentUserLookupId = useSelector(currentUserLookupIdSelector);
  const oCurrentUserName = useSelector(currentUserFirstLastNameSelector);
  const sCurrentUserEmail = useSelector(selectCurrentUserEmail);
  const bIsAuthenticated = useIsUserAuthenticated();
  const [sFirstEventRegStep] = useSelector((state) =>
    selectEventRegNavSteps(state, bIsAuthenticated)
  );
  const aEventsInCart = useSelector(selectAllEventsInCart);
  const aSubEventsInCart = useSelector(
    selectSubEventsByMainEventLookupId(oEvent.LOOKUPID)
  );

  const bHasSubEvents = oEvent?.SUB_EVENTS?.length > 0;

  const oInitialValues = useMemo(
    () => fnBuildInitialValues(oEvent?.LOOKUPID),
    [oEvent]
  );

  const fnHandleContinue = () => {
    // The current event registration checkout flow only supports registering
    // for sub events from one main event at a time.
    // Clear out any other events in the cart.
    const aUnrelatedEventsInCart = aEventsInCart.filter(
      (oEventInCart) =>
        oEventInCart.oDetails.oEvent.MAIN_EVENT?.LOOKUPID !== oEvent?.LOOKUPID
    );
    aUnrelatedEventsInCart.forEach((oEventInCart) => {
      fnRemoveEventFromCart(oEventInCart.sId);
    });

    if (bIsAuthenticated) {
      fnDispatch(
        CheckoutActions.update({
          fullName: oCurrentUserName.sName,
          firstName: oCurrentUserName.sPreferredName,
          lastName: oCurrentUserName.sLastName,
          email: sCurrentUserEmail,
          userLookupId: sCurrentUserLookupId,
        })
      );

      aSubEventsInCart.forEach((oSubEventInCart) => {
        const bUserIsAlreadyInCartForSubEvent =
          sCurrentUserLookupId &&
          oSubEventInCart.oDetails.aGuests.find(
            (oGuest) => oGuest.LOOKUPID === sCurrentUserLookupId
          );
        if (bUserIsAlreadyInCartForSubEvent) {
          return;
        }
        fnAddGuestToEventInCart(
          `${oCurrentUserName.sPreferredName} ${oCurrentUserName.sLastName}`,
          sCurrentUserLookupId,
          oSubEventInCart.sId,
          null,
          'currentHost'
        );
      });
      fnSetHasSubmittedForm(true);
    } else {
      fnSetShowSignInOrContinueAsGuestModal(true);
    }
  };

  // Navigate to the first step in the event registration flow
  // Doing this in a useEffect instead of in the submit handler allows the
  // `selectEventRegNavSteps` selector to re-calculate against the new event(s) in the cart.
  useEffect(() => {
    if (bHasSubmittedForm && sFirstEventRegStep) {
      fnNavigate(sFirstEventRegStep);
    }
  }, [bHasSubmittedForm, sFirstEventRegStep, fnNavigate]);

  return (
    <>
      <EventDetailsPageHeader oEvent={oEvent} />
      <div>
        {oEvent?.DESCRIPTION && (
          <EventDescription
            sDescription={oEvent.DESCRIPTION}
            bIncludeHeader={false}
          />
        )}
        <EventRegistrationWindowClose
          registrationCloseDateTime={oEvent?.REGISTRATION?.CLOSE_DATETIME}
          timeZone={oEvent?.TIME_ZONE}
        />
        <EventRefundPolicy />
        {bHasSubEvents ? (
          <div className='multiLevelEventDetails__subEventsWrapper'>
            <h3 className='t-header4'>
              <FontAwesomeIcon icon={oIconLibrary.fontAwesome.mapWithMarker} />{' '}
              Events
            </h3>

            <Formik
              initialValues={oInitialValues}
              onSubmit={fnHandleContinue}
              validationSchema={oSchema}
            >
              {(formikProps) => (
                <Form id={sFormId} className='multiLevelEventDetails__form'>
                  <>
                    <p>Select the events you would like to attend.</p>
                    <ul className='multiLevelEventDetails__subEventsList'>
                      {oEvent.SUB_EVENTS.map((oSubEvent) => {
                        const bIsGrouping =
                          (oSubEvent.SUB_EVENTS || []).length > 0;
                        if (bIsGrouping) {
                          return (
                            <Accordion
                              mTitle={oSubEvent.TITLE}
                              sTitleIcon='mapMarker'
                              className='multiLevelEventDetails__subEventsGrouping'
                              key={oSubEvent.ID}
                            >
                              {oSubEvent.SUB_EVENTS.map((oSubSubEvent) => (
                                <SubEventDetailCard
                                  key={oSubSubEvent.ID}
                                  oSubEvent={oSubSubEvent}
                                />
                              ))}
                            </Accordion>
                          );
                        }
                        return (
                          <SubEventDetailCard
                            key={oSubEvent.ID}
                            oSubEvent={oSubEvent}
                          />
                        );
                      })}
                    </ul>
                  </>

                  {formikProps.errors?.subEvents && (
                    <p className='form__overallError'>
                      {formikProps.errors?.subEvents}
                    </p>
                  )}
                </Form>
              )}
            </Formik>
            <SelectedEventsList sMainEventLookupId={oEvent.LOOKUPID} />
            <MultiLevelEventDetailsFooter oEvent={oEvent} />
          </div>
        ) : (
          <div className='subEventDetailCard'>
            <p className='notInvited__contact'>
              <strong>{oEvent?.TITLE}</strong> does not have any events that you
              can register for at this time.
            </p>
            <p className='notInvited__contact'>
              If there&apos;s been a mistake, please contact{' '}
              {oEvent.CONTACT.NAME} at{' '}
              <a href={`mailto:${oEvent.CONTACT.EMAIL}`}>
                {oEvent.CONTACT.EMAIL}
              </a>
            </p>
          </div>
        )}
      </div>
    </>
  );
};

MultiLevelEventDetails.propTypes = {
  oEvent: PropTypes.shape({
    TITLE: PropTypes.string.isRequired,
    CONTACT: PropTypes.shape({
      NAME: PropTypes.string.isRequired,
      EMAIL: PropTypes.string.isRequired,
    }),
    DESCRIPTION: PropTypes.string,
    TIME_ZONE: PropTypes.string.isRequired,
    SUB_EVENTS: PropTypes.arrayOf(
      PropTypes.shape({
        ID: PropTypes.string.isRequired,
        TITLE: PropTypes.string.isRequired,
        REGISTRATION: PropTypes.shape({
          CLOSE_DATETIME: PropTypes.string.isRequired,
        }),
      })
    ),
  }).isRequired,
  fnSetShowSignInOrContinueAsGuestModal: PropTypes.func.isRequired,
};

export default MultiLevelEventDetails;
